import { DirectionsBike, DirectionsRun, Hotel, Warning } from '@mui/icons-material';
import {
  Alert,
  Box,
  Chip,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Slider,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import type {
  DayOfWeek,
  UserProfile,
  WeeklyWorkoutPreferences as WeeklyWorkoutPreferencesType,
  WorkoutPreferenceType,
} from '@shared/types';
import React from 'react';

interface WeeklyWorkoutPreferencesProps {
  profile: UserProfile;
  onChange: (updatedProfile: Partial<UserProfile>) => void;
  onValidationChange?: (isValid: boolean) => void;
}

const DAYS_OF_WEEK: DayOfWeek[] = [
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday',
];

const WORKOUT_PREFERENCES: Array<{
  value: WorkoutPreferenceType;
  label: string;
  icon: React.ReactNode;
  color: string;
}> = [
  { value: 'rest', label: 'Rest', icon: <Hotel fontSize="small" />, color: '#9e9e9e' },
  {
    value: 'base run',
    label: 'Base Run',
    icon: <DirectionsRun fontSize="small" />,
    color: '#4caf50',
  },
  {
    value: 'base ride',
    label: 'Base Ride',
    icon: <DirectionsBike fontSize="small" />,
    color: '#4caf50',
  },
  {
    value: 'long run',
    label: 'Long Run',
    icon: <DirectionsRun fontSize="small" />,
    color: '#ff9800',
  },
  {
    value: 'long ride',
    label: 'Long Ride',
    icon: <DirectionsBike fontSize="small" />,
    color: '#ff9800',
  },
  {
    value: 'hard run',
    label: 'Hard Run',
    icon: <DirectionsRun fontSize="small" />,
    color: '#f44336',
  },
  {
    value: 'hard ride',
    label: 'Hard Ride',
    icon: <DirectionsBike fontSize="small" />,
    color: '#f44336',
  },
];

const WeeklyWorkoutPreferences: React.FC<WeeklyWorkoutPreferencesProps> = ({
  profile,
  onChange,
  onValidationChange,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const handlePreferenceChange = (day: DayOfWeek, preference: WorkoutPreferenceType) => {
    const currentPrefs = profile.weeklyWorkoutPreferences || {
      Monday: ['rest'],
      Tuesday: ['rest'],
      Wednesday: ['rest'],
      Thursday: ['rest'],
      Friday: ['rest'],
      Saturday: ['rest'],
      Sunday: ['rest'],
    };

    const updatedPreferences: WeeklyWorkoutPreferencesType = {
      ...currentPrefs,
      [day]: [preference], // Store as single-item array
    };

    onChange({ weeklyWorkoutPreferences: updatedPreferences });
  };

  // Helper to get first preference from array (for single-select UI)
  const getPreferenceForDay = (day: DayOfWeek): WorkoutPreferenceType => {
    const pref = profile.weeklyWorkoutPreferences?.[day];
    if (Array.isArray(pref)) {
      return pref[0] || 'rest';
    }
    // Legacy single-value support
    return (pref as unknown as WorkoutPreferenceType) || 'rest';
  };

  const getPreferenceConfig = (preference: WorkoutPreferenceType | undefined) => {
    return WORKOUT_PREFERENCES.find(p => p.value === preference) || WORKOUT_PREFERENCES[0];
  };

  const handleTrainingRatioChange = (value: number) => {
    const runPercent = value;
    const bikePercent = 100 - value;
    onChange({
      trainingRatio: { running: runPercent, cycling: bikePercent },
    });
  };

  const runPercent = profile.trainingRatio?.running ?? 100;
  const bikePercent = profile.trainingRatio?.cycling ?? 0;

  // Helper to get first preference from array (for validation)
  const getFirstPref = (pref: WorkoutPreferenceType[] | undefined): WorkoutPreferenceType => {
    if (Array.isArray(pref)) {
      return pref[0] || 'rest';
    }
    // Legacy single-value support
    return (pref as unknown as WorkoutPreferenceType) || 'rest';
  };

  // Calculate actual workout days from preferences
  const preferences = (profile.weeklyWorkoutPreferences ||
    {}) as Partial<WeeklyWorkoutPreferencesType>;
  const trainingDays = DAYS_OF_WEEK.filter(day => {
    const pref = getFirstPref(preferences[day]);
    return pref && pref !== 'rest';
  });
  const runDays = DAYS_OF_WEEK.filter(day => {
    const pref = getFirstPref(preferences[day]);
    return pref && pref.includes('run');
  });
  const rideDays = DAYS_OF_WEEK.filter(day => {
    const pref = getFirstPref(preferences[day]);
    return pref && pref.includes('ride');
  });

  const totalTrainingDays = trainingDays.length;
  const actualRunDays = runDays.length;
  const actualRideDays = rideDays.length;

  // Calculate expected days based on ratio
  const expectedRunDays = Math.round((runPercent / 100) * totalTrainingDays);
  const expectedRideDays = Math.round((bikePercent / 100) * totalTrainingDays);

  // Validate ratio match (allow ±1 day tolerance)
  const runDaysValid =
    totalTrainingDays === 0 ||
    Math.abs(actualRunDays - expectedRunDays) <= 1 ||
    (runPercent === 100 && actualRideDays === 0) ||
    (runPercent === 0 && actualRunDays === 0);

  const rideDaysValid =
    totalTrainingDays === 0 ||
    Math.abs(actualRideDays - expectedRideDays) <= 1 ||
    (bikePercent === 100 && actualRunDays === 0) ||
    (bikePercent === 0 && actualRideDays === 0);

  const isValid = runDaysValid && rideDaysValid;

  // Notify parent of validation state whenever it changes
  React.useEffect(() => {
    onValidationChange?.(isValid);
  }, [isValid, onValidationChange, profile.weeklyWorkoutPreferences, profile.trainingRatio]);

  // Get available workout types based on current counts and ratio
  const getAvailableWorkoutTypes = (
    currentDayPreference: WorkoutPreferenceType
  ): WorkoutPreferenceType[] => {
    // Always allow rest
    const available: WorkoutPreferenceType[] = ['rest'];

    // 100% run or bike means ONLY those types are available
    if (runPercent === 100) {
      available.push('base run', 'long run', 'hard run');
      return available;
    }

    if (bikePercent === 100) {
      available.push('base ride', 'long ride', 'hard ride');
      return available;
    }

    // For mixed ratios, check if we can add more run or ride days
    const canAddRunDay =
      currentDayPreference?.includes('run') || actualRunDays < expectedRunDays + 1;

    const canAddRideDay =
      currentDayPreference?.includes('ride') || actualRideDays < expectedRideDays + 1;

    if (canAddRunDay || totalTrainingDays === 0) {
      available.push('base run', 'long run', 'hard run');
    }

    if (canAddRideDay || totalTrainingDays === 0) {
      available.push('base ride', 'long ride', 'hard ride');
    }

    return available;
  };

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Set your preferred workout type for each day. The engine will prioritize these preferences
        while maintaining optimal training balance.
      </Typography>

      {/* Training Ratio Slider */}
      <Box sx={{ mb: 3, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
        <Typography variant="subtitle2" fontWeight="medium" gutterBottom>
          Training Mix
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
          Adjust your run/bike training ratio
        </Typography>
        <Box sx={{ px: 1 }}>
          <Slider
            value={runPercent}
            onChange={(_, value) => handleTrainingRatioChange(value as number)}
            min={0}
            max={100}
            step={25}
            marks={[
              { value: 0, label: '100% 🚴' },
              { value: 50, label: '50/50' },
              { value: 100, label: '100% 🏃' },
            ]}
            valueLabelDisplay="auto"
            valueLabelFormat={value => `${100 - value}% 🚴/ ${value}% 🏃`}
            sx={{
              '& .MuiSlider-markLabel': {
                fontSize: isMobile ? '0.65rem' : '0.75rem',
              },
            }}
          />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1, gap: 1 }}>
            <Chip
              icon={<DirectionsBike fontSize="small" />}
              label={`${bikePercent}% Cycling`}
              size="small"
              sx={{
                bgcolor: 'info.main',
                color: 'white',
                '& .MuiChip-icon': { color: 'white' },
              }}
            />
            <Chip
              icon={<DirectionsRun fontSize="small" />}
              label={`${runPercent}% Running`}
              size="small"
              sx={{
                bgcolor: 'success.main',
                color: 'white',
                '& .MuiChip-icon': { color: 'white' },
              }}
            />
          </Box>
        </Box>
      </Box>

      <Divider sx={{ mb: 2 }} />

      <Typography variant="subtitle2" fontWeight="medium" gutterBottom>
        Daily Workout Preferences
      </Typography>

      {/* Validation Alert */}
      {totalTrainingDays > 0 && !isValid && (
        <Alert severity="warning" icon={<Warning />} sx={{ mb: 2 }}>
          <Typography variant="body2" fontWeight="medium">
            Training days don't match your {runPercent}% run / {bikePercent}% bike ratio
          </Typography>
          <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
            Expected: ~{expectedRunDays} run day{expectedRunDays !== 1 ? 's' : ''} and ~
            {expectedRideDays} ride day{expectedRideDays !== 1 ? 's' : ''}
            <br />
            Current: {actualRunDays} run day{actualRunDays !== 1 ? 's' : ''} and {actualRideDays}{' '}
            ride day{actualRideDays !== 1 ? 's' : ''}
          </Typography>
        </Alert>
      )}

      <Grid container spacing={isMobile ? 1.5 : 2}>
        {DAYS_OF_WEEK.map(day => {
          const currentPreference = getPreferenceForDay(day);
          const config = getPreferenceConfig(currentPreference);

          return (
            <Grid key={day} size={{ xs: 12, sm: 6 }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  p: isMobile ? 1 : 1.5,
                  borderRadius: 1,
                  bgcolor: 'background.paper',
                  border: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <Box sx={{ minWidth: isMobile ? 60 : 80 }}>
                  <Typography variant={isMobile ? 'body2' : 'subtitle2'} fontWeight="medium" noWrap>
                    {isMobile ? day.slice(0, 3) : day}
                  </Typography>
                </Box>

                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                  <FormControl fullWidth size="small">
                    <InputLabel id={`${day}-preference-label`} sx={{ display: 'none' }}>
                      {day} Preference
                    </InputLabel>
                    <Select
                      labelId={`${day}-preference-label`}
                      value={currentPreference}
                      onChange={e =>
                        handlePreferenceChange(day, e.target.value as WorkoutPreferenceType)
                      }
                      aria-label={`${day} workout preference`}
                      renderValue={() => (
                        <Chip
                          icon={config.icon as React.ReactElement}
                          label={isMobile ? config.label.split(' ')[0] : config.label}
                          size="small"
                          sx={{
                            bgcolor: config.color,
                            color: 'white',
                            height: isMobile ? 24 : 28,
                            '& .MuiChip-icon': {
                              color: 'white',
                            },
                            '& .MuiChip-label': {
                              px: isMobile ? 0.5 : 1,
                              fontSize: isMobile ? '0.75rem' : '0.8125rem',
                            },
                          }}
                        />
                      )}
                      sx={{
                        '& .MuiSelect-select': {
                          py: 0.5,
                          display: 'flex',
                          alignItems: 'center',
                        },
                      }}
                    >
                      {WORKOUT_PREFERENCES.map(pref => {
                        const availableTypes = getAvailableWorkoutTypes(currentPreference);
                        const isAvailable = availableTypes.includes(pref.value);
                        const isCurrentSelection = pref.value === currentPreference;

                        return (
                          <MenuItem
                            key={pref.value}
                            value={pref.value}
                            disabled={!isAvailable && !isCurrentSelection}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Box
                                sx={{
                                  color:
                                    !isAvailable && !isCurrentSelection
                                      ? 'action.disabled'
                                      : pref.color,
                                }}
                              >
                                {pref.icon}
                              </Box>
                              <Typography
                                variant="body2"
                                color={
                                  !isAvailable && !isCurrentSelection
                                    ? 'text.disabled'
                                    : 'text.primary'
                                }
                              >
                                {pref.label}
                                {!isAvailable && !isCurrentSelection && ' (not available)'}
                              </Typography>
                            </Box>
                          </MenuItem>
                        );
                      })}
                    </Select>
                  </FormControl>
                </Box>
              </Box>
            </Grid>
          );
        })}
      </Grid>

      {/* Summary Stats */}
      <Box sx={{ mt: 2, p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
        <Grid container spacing={2}>
          <Grid size={{ xs: 6, sm: 3 }}>
            <Typography variant="caption" color="text.secondary">
              🚴 / 🏃
            </Typography>
            <Typography variant="body2" fontWeight="medium">
              {Math.round((actualRideDays / (actualRunDays + actualRideDays)) * 100) || 0}%{' / '}
              {Math.round((actualRunDays / (actualRunDays + actualRideDays)) * 100) || 0}%
            </Typography>
          </Grid>

          <Grid size={{ xs: 6, sm: 3 }}>
            <Typography variant="caption" color="text.secondary">
              🚴‍♂️ Days
            </Typography>
            <Typography variant="body2" fontWeight="medium">
              {actualRideDays}
            </Typography>
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <Typography variant="caption" color="text.secondary">
              🏃Days
            </Typography>
            <Typography variant="body2" fontWeight="medium">
              {actualRunDays}
            </Typography>
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <Typography variant="caption" color="text.secondary">
              🛌 Days
            </Typography>
            <Typography variant="body2" fontWeight="medium">
              {7 - totalTrainingDays}
            </Typography>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default WeeklyWorkoutPreferences;
